import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Button,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon,
  Assignment as AssignmentIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import inventoryService, { InventoryAlert } from '../services/inventoryService';

const InventoryAlertsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [alerts, setAlerts] = useState<InventoryAlert[]>([]);
  const [filteredAlerts, setFilteredAlerts] = useState<InventoryAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  useEffect(() => {
    loadAlerts();
  }, []);

  useEffect(() => {
    const filter = searchParams.get('filter');
    setActiveFilter(filter);
    applyFilter(filter);
  }, [searchParams, alerts]);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const alertsData = await inventoryService.getInventoryAlerts();
      // Filter alerts to only show products with issues and available inventory > 0
      const validAlerts = alertsData.filter((alert) => {
        // Don't show expired alerts for products with 0 inventory
        if (alert.type === 'expiring_soon' && alert.casesAvailable === 0) {
          return false;
        }
        return true;
      });
      setAlerts(validAlerts);
    } catch (err) {
      console.error('Error loading alerts:', err);
      setError('Failed to load inventory alerts');
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (filter: string | null) => {
    if (!filter) {
      setFilteredAlerts(alerts);
      return;
    }

    let filtered: InventoryAlert[] = [];

    switch (filter) {
      case 'shelf-life':
        filtered = alerts.filter(
          (alert) => alert.type === 'expired' || alert.type === 'expiring_soon'
        );
        break;
      case 'low-stock':
        filtered = alerts.filter((alert) => alert.type === 'low_stock');
        break;
      default:
        filtered = alerts;
        break;
    }

    setFilteredAlerts(filtered);
  };

  const getFilterTitle = () => {
    switch (activeFilter) {
      case 'shelf-life':
        return 'Shelf Life Alerts';
      case 'low-stock':
        return 'Low Stock Alerts';
      default:
        return 'Inventory Alerts';
    }
  };

  const handleAlertClick = (alert: InventoryAlert) => {
    // Navigate to inventory page with the product selected
    navigate(
      `/inventory?productId=${alert.productId}&search=${encodeURIComponent(alert.productCode)}`
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography
            variant="h4"
            component="h1"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <WarningIcon />
            {getFilterTitle()}
          </Typography>
          <Button component={Link} to="/inventory" variant="outlined" startIcon={<ArrowBackIcon />}>
            Back to Inventory
          </Button>
        </Stack>
        {activeFilter && (
          <Chip
            label={`Filter: ${activeFilter === 'shelf-life' ? 'Shelf Life' : 'Low Stock'}`}
            color="primary"
            variant="outlined"
            onDelete={() => {
              navigate('/inventory-alerts');
            }}
          />
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Alerts Grid */}
      <Grid container spacing={2}>
        {filteredAlerts.map((alert) => (
          <Grid item xs={12} key={`${alert.type}-${alert.productId}`}>
            <Card
              sx={{
                cursor: 'pointer',
                '&:hover': { boxShadow: 3 },
                borderLeft: `4px solid ${
                  alert.severity === 'critical'
                    ? '#f44336'
                    : alert.severity === 'warning'
                      ? '#ff9800'
                      : '#4caf50'
                }`,
              }}
              onClick={() => handleAlertClick(alert)}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h6" component="h3">
                      {alert.productName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {alert.productCode}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {alert.message}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Available: {alert.casesAvailable} cases, {alert.unitsAvailable} units
                      {alert.daysUntilExpiry !== null &&
                        ` • ${alert.daysUntilExpiry} days until expiry`}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      icon={
                        alert.severity === 'critical' ? (
                          <ErrorIcon />
                        ) : alert.severity === 'warning' ? (
                          <WarningIcon />
                        ) : (
                          <CheckCircleIcon />
                        )
                      }
                      label={alert.severity.toUpperCase()}
                      color={
                        alert.severity === 'critical'
                          ? 'error'
                          : alert.severity === 'warning'
                            ? 'warning'
                            : 'success'
                      }
                      size="small"
                    />
                    <Chip label="0 ORDERS" color="default" size="small" icon={<AssignmentIcon />} />
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {filteredAlerts.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <InventoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Inventory Alerts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All products are properly stocked and within expiry guidelines.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default InventoryAlertsPage;
